import { FC, useContext } from "react";

import styles from "./FlashOfferRow.module.css";
import FlashPromotionContext from "~/contexts/FlashPromotionContext";
import OrderContext from "~/contexts/OrderContext";
import TaxContext from "~/contexts/TaxContext";

/**
 * The flash offer's progress in the cart, and the guard on its reward.
 *
 * Two jobs, because they are the same piece of information seen from either
 * side:
 *
 * 1. Progress. "You're $12.00 away" is far more actionable than "spend $63" —
 *    it names a gap closeable with one more item rather than a number to do
 *    arithmetic against, and it shrinks as they add things.
 *
 * 2. The guard. An unqualified reward line is charged at FULL PRICE:
 *    api/helpers/promotionHelpers.go returns nil when requiredPrice is not met,
 *    so no discount attaches and the line prices normally. Since tapping the
 *    card adds that line automatically, a customer can be sitting on a "free"
 *    item they are about to pay for. That must never be silent.
 *
 * Deliberately a loud, actionable warning rather than a hard block on checkout:
 * someone who actually wants the item at full price is entitled to buy it. What
 * they are not entitled to is being surprised.
 */
const FlashOfferRow: FC = () => {
  const { flashPromotion } = useContext(FlashPromotionContext);
  const { orderSummary, removeFlashOffer, readonlyCart } =
    useContext(OrderContext);
  const { formatAmount } = useContext(TaxContext);

  // Only when this campaign's code is the one on the order. A code the customer
  // typed by hand is the PromoCode component's business, not ours.
  if (
    !flashPromotion ||
    orderSummary.promoCode !== flashPromotion.voucherUnlockCode
  ) {
    return null;
  }

  const shortfall =
    flashPromotion.minimumSpend - orderSummary.totalBeforeDiscount;
  const unlocked = shortfall <= 0;

  // Which cart line is the reward? Taken from the promotion the code unlocked,
  // so it follows whatever the operator configured rather than a guess.
  const promotion = orderSummary.unlockedPromotions?.find(
    (p) => p.code === flashPromotion.promotionCode
  );
  const rewardLine = promotion?.productCodes.length
    ? orderSummary.lines?.find((line) =>
        promotion.productCodes.includes(line.item?.productCode)
      )
    : undefined;

  const chargingForTheReward = !!rewardLine && !unlocked;

  const removeReward = () => {
    if (readonlyCart || !rewardLine) {
      return;
    }
    // One submit for both: two calls would each build from the same stale
    // orderSummary, and the second would put the line back
    removeFlashOffer(rewardLine.id);
  };

  return (
    <div
      className={styles.row}
      data-unlocked={unlocked}
      data-warning={chargingForTheReward}
    >
      <div className={styles.main}>
        <span className={styles.name}>{flashPromotion.header}</span>
        <span className={styles.status}>
          {unlocked ? "unlocked" : `${formatAmount(shortfall)} away`}
        </span>
      </div>

      {chargingForTheReward && (
        <div className={styles.warning}>
          <span>
            {/* "spend $20" implies $20 of OTHER items. The engine measures the
                cart total, and the reward's own price is part of it, so say
                cart — true either way, and it does not promise a bar the engine
                will not enforce. */}
            You&rsquo;ll be charged for this until your cart reaches{" "}
            {formatAmount(flashPromotion.minimumSpend)}.
          </span>
          <button
            type="button"
            className={styles.remove}
            onClick={removeReward}
            disabled={readonlyCart}
          >
            Remove it
          </button>
        </div>
      )}
    </div>
  );
};

export default FlashOfferRow;
